/**
 * ROLE-USERS.WIDGET
 * Membres d'un rôle : collaborateurs dont les attributs valident ses conditions
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faCopy, faEllipsisV, faEye, faPaperPlane, faPencilAlt, faUserCircle } from "@fortawesome/free-solid-svg-icons"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { Role } from "@/redux/role.types"
import { User } from "@/redux/user.types"
import { roleFetchUsers } from "@/redux/role.actions"
import { userEditAndUpdate, userInvite, userUpdatePasswordProvided_AsAdmin } from "@/redux/user.actions"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"
import ListContainer from "@/components/list-container"
import ListItem from "@/components/list-item"
import ListIcon from "@/components/list-icon"
import ListCell from "@/components/list-cell"
import ListButton from "@/components/list-button"
import Button from "@/components/button"
import PageLoader from "@/components/page-loader"
import UserPopulations from "@/components/user-populations"
import SendInvitationButton from "@/components/SendInvitationButton"
import ModalConfirm from "@/components/modal-confirm"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"
import LoadingModal from "@/modals/loading.modal"
import copyLink from "@/utils/copy-link.utils"
import { toast } from "react-toastify"

//Nombre de membres affichés par page
const LIMIT = 10

//Actions du menu contextuel. Pas d'ajout ni de retrait : l'appartenance au
//rôle est decidee par ses populations, pas ligne par ligne.
const ACTION_PREVIEW = "ACTION_PREVIEW"
const ACTION_COPY_LINK = "ACTION_COPY_LINK"
const ACTION_EDIT = "ACTION_EDIT"

//Code du mail d'invitation : un membre d'un acces restreint arrive sur le
//tableau de bord avec le meme email qu'un observateur.
const EMAIL_CODE = "inviteSurveyObserver"

//Taille des pages parcourues pour rassembler tous les membres avant un envoi
//groupe (le endpoint plafonne a 100).
const INVITE_PAGE = 100

export default function RoleUsersWidget({ role }: { role: Role }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const session = useAppSelector((state) => state._session)
  const accountName = session.accountName
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState(0)
  const [search, setSearch] = useState("")
  const [offset, setOffset] = useState(0)
  const [status, setStatus] = useState(STATUS_LOADING)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [modal, setModal] = useState<"edit" | "loading" | "email" | "inviteAll" | null>(null)
  const [refresh, setRefresh] = useState(0)
  const [recipients, setRecipients] = useState<User[]>([])
  const [sending, setSending] = useState(false)

  const actions = [
    { id: ACTION_PREVIEW, label: t("roles_preview"), icon: faEye },
    { id: ACTION_COPY_LINK, label: t("utils_copy_link"), icon: faCopy },
    { id: ACTION_EDIT, label: t("user_edit"), icon: faPencilAlt }
  ]

  //Les conditions décident des membres : un changement de règles recharge la
  //liste, et repart de la première page — l'effectif peut avoir fondu.
  const conditionsKey = JSON.stringify(role.conditions)

  useEffect(() => {
    setOffset(0)
  }, [conditionsKey])

  useEffect(() => {
    let cancelled = false
    load()
    async function load() {
      setStatus(STATUS_LOADING)
      const response: any = await dispatch(roleFetchUsers(role.id, search, LIMIT, offset))
      if (cancelled) return
      const list = Array.isArray(response?.list) ? response.list : []
      setUsers(list.map((user: Partial<User>) => new User(user)))
      setCount(response?.error ? 0 : (response?.count ?? 0))
      setStatus(STATUS_LOADED)
    }
    return () => {
      cancelled = true
    }
  }, [dispatch, role.id, conditionsKey, search, offset, refresh])

  //Ouvre le tableau de bord du membre. Comme pour les administrateurs, un mot
  //de passe deja fourni est reinitialise pour pouvoir y acceder.
  async function preview(user: User) {
    if (user.passwordProvided) {
      setModal("loading")
      const response: any = await dispatch(userUpdatePasswordProvided_AsAdmin(user.id))
      setModal(null)
      if (response?.error) return
    }
    window.open(user.getInvitationLink(session, null, true), "_blank")
  }

  function selectAction(user: User, action: string) {
    setCurrentUser(user)
    switch (action) {
      case ACTION_PREVIEW:
        return preview(user)
      case ACTION_COPY_LINK:
        return copyLink(user, session)
      case ACTION_EDIT:
        return setModal("edit")
    }
  }

  //Invitable : une adresse email, jamais invite, jamais connecte. Meme regle
  //que le bouton d'envoi individuel, qui se desactive dans les memes cas.
  const canInvite = (user: User) => Boolean(user.email) && !user.invitedAt && !user.connectedAt

  //L'envoi groupe vise tous les membres du role, pas seulement la page
  //affichee : on parcourt donc la liste entiere avant de demander confirmation.
  async function prepareInviteAll() {
    if (sending) return
    setModal("loading")
    const all: User[] = []
    for (let page = 0; ; page++) {
      const response: any = await dispatch(roleFetchUsers(role.id, "", INVITE_PAGE, page * INVITE_PAGE))
      const list = Array.isArray(response?.list) ? response.list : []
      all.push(...list.map((user: Partial<User>) => new User(user)))
      if (list.length < INVITE_PAGE || all.length >= (response?.count ?? 0)) break
    }
    const invitable = all.filter(canInvite)
    if (!invitable.length) {
      setModal(null)
      toast(t("roles_invite_all_none"))
      return
    }
    setRecipients(invitable)
    setModal("inviteAll")
  }

  async function sendInvitations() {
    if (sending) return
    setSending(true)
    let sent = 0
    for (const user of recipients) {
      const response: any = await dispatch(
        userInvite(user.id, session.userName, user.getInvitationLink(session), EMAIL_CODE, "", [])
      )
      if (!response?.error) {
        sent++
        await dispatch(userEditAndUpdate(user, "invitedAt", new Date().getTime()))
      }
    }
    setSending(false)
    setModal(null)
    setRecipients([])
    toast(t("roles_invite_all_done", { count: sent }))
    setRefresh((value) => value + 1)
  }

  //Valeurs du membre pour les attributs de périmètre du rôle
  function scopeOf(user: User) {
    const populations = [
      ...role.scopePopulations,
      ...role.scopeFilterNames
        .map((filterName) => ({ filterName, name: user.attributes?.[filterName] }))
        .filter((population) => population.name)
    ]
    return [...new Map(populations.map((population) => [JSON.stringify([population.filterName, population.name]), population])).values()]
  }

  return (
    <>
      {modal === "loading" && <LoadingModal />}

      {modal === "email" && currentUser && (
        <AdmSendEmailModal
          emailCode={EMAIL_CODE}
          currentUser={currentUser}
          onClose={() => {
            setModal(null)
            setRefresh((value) => value + 1)
          }}
          textBold={t("utils_next_ask")}
          text={t("utils_email_ask_confirm", { email: currentUser.email, name: currentUser.username })}
        />
      )}

      {modal === "inviteAll" && (
        <ModalConfirm
          status={sending ? STATUS_SAVING : STATUS_LOADED}
          onNo={() => {
            if (!sending) {
              setModal(null)
              setRecipients([])
            }
          }}
          onYes={sendInvitations}
          text={
            recipients.length === 1
              ? t("roles_invite_all_confirm_one")
              : t("roles_invite_all_confirm", { count: recipients.length })
          }
          textBold={t("utils_next_ask")}
        />
      )}

      {modal === "edit" && currentUser && (
        <AdmEditUserModal
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onNext={() => {
            setModal(null)
            setRefresh((value) => value + 1)
          }}
        />
      )}

      <ListContainer
        countLimit={LIMIT}
        countSearch={count}
        isFooterActive
        onSearch={(value: string) => {
          setOffset(0)
          setSearch(value)
        }}
        onSetPage={setOffset}
        extraActions={
          <Button className="" isWithBorder isFullWidth icon={faPaperPlane} onClick={prepareInviteAll}>
            {t("roles_invite_all")}
          </Button>
        }
        status={status}
        title={t("roles_users")}>
        <PageLoader status={status}>
          {users.length === 0 && (
            <ListItem>
              {search.length > 0
                ? t("search_no_result", { search })
                : t(role.conditions.length === 0 ? "roles_no_members" : "roles_no_members_match")}
            </ListItem>
          )}

          {users.map((user) => (
            <ListItem key={user.id}>
              <ListIcon fallbackIcon={faUserCircle} image={user.imageUrl} />
              <ListCell width={220} text={user.username} />
              <div className="flex1">
                <UserPopulations
                  populations={scopeOf(user)}
                  emptyLabel={role.scopeFilterNames.length === 0 && role.scopePopulations.length === 0 ? accountName : null}
                />
              </div>

              <SendInvitationButton
                user={user}
                onClick={() => {
                  setCurrentUser(user)
                  setModal("email")
                }}
              />

              <ListButton
                onAction={(action: string) => selectAction(user, action)}
                icon={faEllipsisV}
                actions={actions}
              />
            </ListItem>
          ))}
        </PageLoader>
      </ListContainer>
    </>
  )
}
